import { isWeightBasedProduct, isWeightTotalComplete, sumWeights } from "@repo/shared";
import { useMemo } from "react";
import {
  useAddPortfolioProduct,
  useDeletePortfolioProduct,
  useUpdatePortfolioProduct,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import {
  ProductsWeightsEditor,
  productOption,
  type WeightEditorRow,
  wrapperNamesOf,
} from "../../portfolio-weights";
import { T } from "./portfolio-products-editor.i18n";

type PortfolioProductsEditorProps = {
  collectionId: string;
  portfolio: any;
  products: any[];
  /** Collection wrapper types; empty when the feature is disabled (hides the column). */
  wrapperTypes: any[];
};

export function PortfolioProductsEditor({
  collectionId,
  portfolio,
  products,
  wrapperTypes,
}: PortfolioProductsEditorProps) {
  const localize = useLocalize();
  const t = useT(T);

  const addPortfolioProduct = useAddPortfolioProduct();
  const updatePortfolioProduct = useUpdatePortfolioProduct();
  const deletePortfolioProduct = useDeletePortfolioProduct();

  // Wrapper types against each product (only when the collection has the
  // feature enabled — the page passes an empty list otherwise).
  const rows: WeightEditorRow[] = portfolio.products.map((pp: any) => ({
    id: pp.id,
    productId: pp.productId,
    name: localize(pp.product.nameI18n),
    isin: pp.product.isin,
    weight: Number(pp.weight),
    minBuyAmount: pp.product?.minBuyAmount != null ? Number(pp.product.minBuyAmount) : null,
    wrapperNames: wrapperNamesOf(
      products.find((p: any) => p.id === pp.productId),
      wrapperTypes,
      localize,
    ),
  }));

  // QL-180 — the register can hold hundreds of products, so the picker is
  // searchable. Products already in the portfolio and units-ordered products
  // (not weight-based) are left out.
  const productOptions = useMemo(
    () =>
      products
        .filter(
          (product: any) =>
            !portfolio.products.some((p: any) => p.productId === product.id) &&
            isWeightBasedProduct(product),
        )
        .map((product: any) => productOption(product, localize)),
    [products, portfolio.products, localize],
  );

  const weights = rows.map((row) => row.weight);

  return (
    <ProductsWeightsEditor
      backTo={`/settings/portfolios/${portfolio.portfolioCollectionId}/portfolios-list`}
      backLabel={t.backToPortfolios}
      rows={rows}
      productOptions={productOptions}
      showWrapperTypes={wrapperTypes.length > 0}
      totalWeight={sumWeights(weights)}
      isComplete={isWeightTotalComplete(weights)}
      weightStep="0.1"
      onAdd={(productId, weight) =>
        addPortfolioProduct.mutateAsync({
          collectionId,
          portfolioId: portfolio.id,
          data: { productId, weight },
        })
      }
      onUpdate={(ppId, weight) =>
        updatePortfolioProduct.mutateAsync({
          collectionId,
          portfolioId: portfolio.id,
          ppId,
          data: { weight },
        })
      }
      onRemove={(ppId) =>
        deletePortfolioProduct.mutateAsync({ collectionId, portfolioId: portfolio.id, ppId })
      }
    />
  );
}
